import { camelizeKeys, decamelizeKeys } from 'humps';
import { omit } from 'lodash';
import { purchase as purchaseApi } from 'js-api-client';
import store from '@/store';
import {
  fetchTripsDetails,
  receivePurchase,
  selectSeats,
  setPassengers,
  lockTickets,
  lockTicketsLegacy,
  documentWithoutSize,
  toggleInsurance,
  releaseQuickPurchaseLoading,
} from '@/actions/purchase';
import { setError } from '@/actions';
import { decodePassengers } from 'utils/Reserbus';
import { getQuickPurchasePassengers } from 'utils/quickPurchasePassengers';
import { getQuickPurchaseInsuranceSelection } from 'utils/quickPurchaseInsurance';
import autoSelectSeats from 'utils/purchase/autoSelectSeats';
import quickPurchaseInitialization, {
  getQuickPurchaseItineraryKey,
} from 'utils/purchase/quickPurchaseInitialization';
import smartSeatSelectionResult from 'utils/purchase/smartSeatSelectionResult';
import { selectQuickPurchaseSeats } from 'utils/purchase/quickPurchaseSmartSeatSelection';
import saveConfirmedSeatPreference from 'utils/purchase/seatSelectionConfirmation';
import { resolveQuickPurchaseSeatReferences } from 'utils/purchase/seatReferenceTransport';
import { getBrand2ProcessorMap } from 'utils/documentMaps';
import normalizeRegex from 'utils/normalizeRegex';
import { formatName } from 'utils/purchase/parsePassenger';
import {
  handleBackendFieldError,
  hasParseablePassengerFieldError,
  parsePassengerFieldErrors,
} from 'utils/purchase/parseBackendErrors';
import { setUserTokenForAPI, getWalletTypeForPurchase } from 'utils/loyalty';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import i18n from 'i18next';

const alwaysCurrent = () => true;
const supersededResult = () => ({ success: false, superseded: true });
const bindingRejectedResult = (token) => ({
  success: false,
  bindingRejected: true,
  errorCode: 'TRIP_DETAILS_BINDING_REJECTED',
  token,
});

/**
 * Returns the backend document type for a quick-purchase passenger.
 * @param {object} passenger - Passenger data.
 * @returns {string} Processor document type.
 */
function getPassengerDocumentType(passenger) {
  const brand2ProcessorMap = getBrand2ProcessorMap();
  return brand2ProcessorMap[passenger.documentType] || passenger.documentType;
}

/**
 * Serializes a quick-purchase passenger for the purchase API.
 * @param {object} params - Serialization params.
 * @param {object} params.passenger - Passenger data.
 * @param {object} params.features - Tenant feature flags.
 * @param {number} [params.purchaserNationalityId] - Purchaser nationality id.
 * @param {object} [params.insuranceSelection] - Passenger insurance flags.
 * @returns {object} API-ready passenger data.
 */
function serializeQuickPurchasePassenger({
  passenger,
  features,
  purchaserNationalityId,
  insuranceSelection = {},
}) {
  return {
    ...formatName(passenger),
    ...insuranceSelection,
    ...(features.DOCUMENT_PER_PASSENGER &&
      passenger?.document && {
        document: documentWithoutSize(passenger.document),
      }),
    ...(purchaserNationalityId && {
      nationalityId: purchaserNationalityId,
    }),
    document_type: getPassengerDocumentType(passenger),
    isoCountryCode: passenger.nationality || passenger.isoCountryCode,
    phone: normalizeRegex('[^0-9]', passenger.phone),
  };
}

/**
 * savePassengersForQuickPurchase.
 * Save passengers and lock tickets for the quick purchase flow.
 * Unlike savePassengersWithTickets, this properly awaits lockTickets to prevent
 * race conditions when the QuickCheckout UI becomes interactive.
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} token - Purchase token.
 * @param {object} fields - Passenger and purchaser fields.
 * @param {object} trips - Trips object with departs and optional returns.
 * @param {object} [options] Quick Purchase initialization options.
 * @param {Function} [options.isCurrent] Caller result/request predicate. It remains conjunctive
 * with initialization, smart-result and optional attempt ownership at every async boundary.
 * @param {object} [options.ownership] Current Quick Purchase initialization handle.
 * @param {object} [options.attemptOwnership] Optional caller-owned loading capability.
 * @param {object|null} [options.smartSelection] Retained immutable smart selection.
 * @param {object|null} [options.smartSelectionContext] Retained result-generation context.
 * @param {object} [options.initialization] Current Quick Purchase ownership handle.
 * @param {boolean} [options.retrySmartSelection=false] Explicit passenger-correction retry.
 */
async function savePassengersForQuickPurchase(dispatch, token, fields, trips, options = {}) {
  const {
    isCurrent: suppliedIsCurrent = alwaysCurrent,
    ownership: suppliedOwnership = null,
    attemptOwnership: suppliedAttemptOwnership = null,
    smartSelection = null,
    smartSelectionContext = null,
    initialization = null,
    retrySmartSelection = false,
  } = options;
  if (!suppliedIsCurrent()) return supersededResult();

  const retry = retrySmartSelection
    ? smartSeatSelectionResult.retrySelectionForUnchangedTrip({ token, trips })
    : null;
  const analyticsHandle = initialization || retry?.handle || suppliedOwnership;
  const ownership = suppliedOwnership || analyticsHandle;
  const resultContext = smartSelectionContext || retry?.context || null;
  const ownsSmartResult = Boolean(analyticsHandle && resultContext);
  const isCurrent = ownsSmartResult
    ? () =>
        suppliedIsCurrent() &&
        analyticsHandle.isCurrent?.() === true &&
        resultContext.isCurrent?.() === true &&
        (suppliedAttemptOwnership?.isCurrent?.() ?? true)
    : () =>
        suppliedIsCurrent() &&
        (ownership?.isCurrent?.() ?? true) &&
        (suppliedAttemptOwnership?.isCurrent?.() ?? true);
  if (!isCurrent()) return supersededResult();
  const markSmartResultFailed = () => {
    if (!ownsSmartResult) return;
    smartSeatSelectionResult.markSelectionFailed({
      handle: analyticsHandle,
      token,
      resultContext,
    });
  };

  const { env, features } = store.getState().whitelabelConfig;
  const { availableWallets = [] } = store.getState().purchase.toJS();

  const purchasePayloadBase =
    env.brand === 'gfa'
      ? decamelizeKeys({
          purchaserNationalityId: 1,
          ...omit(fields, 'passengers'),
          email: fields.email,
        })
      : decamelizeKeys({
          purchaserNationalityId: 1,
          omitEmailValidation: true,
          ...omit(fields, 'passengers'),
          email: features.NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD
            ? fields.passengers[0].email
            : fields.email,
        });

  const passengersPayload = decamelizeKeys(
    fields.passengers.map((passenger) => {
      return serializeQuickPurchasePassenger({
        passenger,
        features,
        purchaserNationalityId: purchasePayloadBase.purchaser_nationality_id,
      });
    }),
  );

  setUserTokenForAPI();
  const walletType = getWalletTypeForPurchase();
  const isValidWalletType = walletType && availableWallets?.includes(walletType);

  const purchasePayload = {
    ...purchasePayloadBase,
    wallet_type: isValidWalletType ? walletType : null,
    used_frequent_passengers: fields.passengers.some((p) => p.isFrequentPassenger),
    used_autoselected_passenger: fields.passengers.some((p) => p.preferredPassengerOrder),
  };

  let purchaseResponse;
  try {
    if (!isCurrent()) return supersededResult();
    purchaseResponse = await purchaseApi.update(token, purchasePayload);
    if (!isCurrent()) return supersededResult();
  } catch (error) {
    if (!isCurrent()) return supersededResult();
    markSmartResultFailed();
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:updatePurchaser', token },
    });
    throw error;
  }

  let payload;
  try {
    if (!isCurrent()) return supersededResult();
    if (purchaseResponse.purchase.passengers.length) {
      payload = await purchaseApi.updatePassengers(token, passengersPayload);
    } else {
      passengersPayload.forEach((p) => Reflect.deleteProperty(p, 'id'));
      payload = await purchaseApi.createPassengers(token, passengersPayload);
    }
    if (!isCurrent()) return supersededResult();
  } catch (error) {
    if (!isCurrent()) return supersededResult();
    markSmartResultFailed();
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:savePassengers', token },
    });

    // Quick Purchase reuses frequent/saved passengers without ever validating their date of birth,
    // so a minor carrying a CC reaches this save and platform-purchase refuses it. Route the same
    // parseable field errors savePassengersWithTickets routes: the error then sits on that
    // passenger's own field for the inline-edit form, instead of only reaching the caller as a raw
    // backend key.
    if (hasParseablePassengerFieldError(error)) {
      handleBackendFieldError({
        reason: error,
        dispatch,
        formName: 'passengers',
        errorCode: 202,
      });
    }

    throw error;
  }

  const { passengers } = camelizeKeys(payload);
  if (!isCurrent()) return supersededResult();
  dispatch(setPassengers(passengers, token));

  const reservation = ownsSmartResult
    ? smartSeatSelectionResult.beginReservation({
        handle: analyticsHandle,
        token,
        trips,
        passengers,
        resultContext,
        mode: features.USE_LEGACY_LOCK_TICKETS ? 'legacy' : 'modern',
        onAttemptCanceled: (loadingOwnerId) =>
          dispatch(releaseQuickPurchaseLoading(loadingOwnerId)),
      })
    : null;
  const onReservationOutcome = reservation?.observe;
  const attemptOwnership = reservation?.attemptOwnership || suppliedAttemptOwnership;
  const lockIsCurrent = () => isCurrent() && (attemptOwnership?.isCurrent?.() ?? true);

  try {
    if (!lockIsCurrent()) {
      reservation?.cancel?.();
      return supersededResult();
    }
    if (features.USE_LEGACY_LOCK_TICKETS) {
      const { departs, returns } = trips;
      const departureSeats = departs[0].selectedSeats?.map(({ number }) => number);
      const lockTicketRequests = [
        dispatch(
          lockTicketsLegacy('departs', token, departs, passengers, departureSeats, {
            isCurrent: lockIsCurrent,
            ...(onReservationOutcome && { onReservationOutcome }),
            ownership,
            attemptOwnership,
          }),
        ),
      ];

      if (returns) {
        const returnSeats = returns[0].selectedSeats?.map(({ number }) => number);
        lockTicketRequests.push(
          dispatch(
            lockTicketsLegacy('returns', token, returns, passengers, returnSeats, {
              isCurrent: lockIsCurrent,
              ownership,
              attemptOwnership,
              ...(onReservationOutcome && { onReservationOutcome }),
            }),
          ),
        );
      }

      await Promise.all(lockTicketRequests);
    } else {
      await dispatch(
        lockTickets(token, trips, passengers, {
          preservePaymentOptionSelection: true,
          isCurrent: lockIsCurrent,
          ...(onReservationOutcome && { onReservationOutcome }),
          ownership,
          attemptOwnership,
        }),
      );
    }
    if (!lockIsCurrent()) return supersededResult();
  } catch (error) {
    if (!lockIsCurrent()) return supersededResult();
    markSmartResultFailed();
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:lockTickets', token },
    });
    throw error;
  } finally {
    reservation?.settle?.();
  }

  return { success: true, smartSelection };
}

/**
 * saveQuickCheckoutPassengers action creator.
 * Expose savePassengersForQuickPurchase as a dispatchable thunk for use in QuickCheckout.
 * On re-edits the inner helper takes the updatePassengers branch, preserving passenger ids.
 * @param {string} token - Purchase token.
 * @param {object} fields - Passenger and purchaser fields.
 * @param {object} trips - Trips object with departs and optional returns.
 * @returns {Function} Redux thunk.
 */
export function saveQuickCheckoutPassengers(token, fields, trips, options) {
  return (dispatch) => savePassengersForQuickPurchase(dispatch, token, fields, trips, options);
}

/**
 * Human-readable copy for an orchestration failure.
 *
 * QuickCheckout renders this string verbatim, so a passenger-field 422 would otherwise show the
 * buyer the raw backend key (`passenger.document_type.invalid_for_minor.1`). The seat sentinels the
 * caller branches on (`NOT_ENOUGH_SEATS_*`) are left untouched.
 *
 * @param {Error} error - The error thrown during orchestration.
 * @returns {string} The message to surface.
 */
function quickPurchaseErrorMessage(error) {
  const fieldErrors = parsePassengerFieldErrors(error);
  if (!fieldErrors) return error.message;

  return i18n.t(`notifications:error.${fieldErrors.bannerMessage}`);
}

/**
 * completeQuickPurchase action creator.
 * Complete the quick purchase flow inside QuickCheckout.
 * Fetches trip details, selects seats (user-picked or auto), creates passengers, and locks tickets.
 * Reads frequent passengers directly from Redux store.
 * @param {object} params - passengers, selectedSeatNumbers.
 * @param {string} params.passengers - Encoded passenger string (e.g., "A1", "A2").
 * @param {Array<string>} [params.selectedSeatNumbers] - Seat numbers chosen by the user in seat selector.
 * @param {Array<object>} [params.selectedSeatReferences] - Ordered full seat references.
 * @param {boolean} [params.smartSeatSelectionEnabled=false] Attempt-scoped boolean flag.
 * @param {object} [params.initialization] Generation/token/itinerary ownership handle.
 * @param {Function} [params.isCurrent] Optional result/request predicate, conjunctive with the
 * initialization handle at every nested async boundary.
 * @param {object} [params.attemptOwnership] Optional request-scoped loading capability. CON-1241's
 * `smartSeatSelectionResult.beginReservation` supplies it after passenger save so each reservation
 * or retry owns its loading state within the current initialization generation. A later same-handle
 * attempt must use `createQuickPurchaseAttemptLoadingOwnerId` with a unique concurrent request key
 * and authorize cleanup only when no newer attempt replaced it.
 * @returns {Function} Redux thunk that resolves with { success, error }.
 */
export default function completeQuickPurchase({
  passengers,
  selectedSeatNumbers,
  selectedSeatReferences,
  smartSeatSelectionEnabled = false,
  initialization,
  isCurrent: callerIsCurrent = alwaysCurrent,
  attemptOwnership,
}) {
  return async (dispatch) => {
    let ownership = initialization;
    let token;
    const isCurrent = () =>
      callerIsCurrent() &&
      (ownership?.isCurrent?.() ?? true) &&
      (attemptOwnership?.isCurrent?.() ?? true);

    try {
      const purchaseState = store.getState().purchase.toJS();
      token = purchaseState.token;
      if (!ownership) {
        ownership = quickPurchaseInitialization.begin({
          token,
          itineraryKey: getQuickPurchaseItineraryKey(purchaseState),
          purchase: purchaseState,
        });
      }
      smartSeatSelectionResult.ensureBooking({
        handle: ownership,
        token,
        itineraryKey: ownership.itineraryKey,
      });
      if (!isCurrent()) return supersededResult();

      const userPrefs = store.getState().userPreferences.toJS();
      const frequentPassengers = userPrefs.frequentPassengers?.passengers || [];
      const { features } = store.getState().whitelabelConfig;

      const adapted = getQuickPurchasePassengers(frequentPassengers, features);
      const decodedPassengers = decodePassengers(passengers);
      const passengerCount = decodedPassengers.adult || 1;

      // 1. Fetch trip details (bus layouts)
      if (!isCurrent()) return supersededResult();
      await dispatch(
        fetchTripsDetails({
          departureFragments: purchaseState.departs.fragments,
          returnFragments: purchaseState.returns?.fragments || [],
          isCurrent: callerIsCurrent,
          ownership,
          attemptOwnership,
        }),
      );
      if (!isCurrent()) return supersededResult();

      const purchaseAfterDetails = store.getState().purchase.toJS();

      // 2. Select seats for departure: prefer user-picked seats, fallback to auto-select
      const departureLayout = purchaseAfterDetails.departs.layout;
      const departureTripSlug = purchaseAfterDetails.departs.tripSlug;
      const departureDiagramType = purchaseAfterDetails.departs.diagramType;
      if (!quickPurchaseInitialization.bindTripDetails(ownership, purchaseAfterDetails)) {
        if (!isCurrent() || ownership?.getStatus?.() !== 'current') return supersededResult();
        return bindingRejectedResult(token);
      }
      if (!isCurrent()) return supersededResult();

      const referenceOptions = {
        layout: departureLayout,
        tripSlug: departureTripSlug,
        passengerCount,
        diagramType: ['bus', 'van'].includes(departureDiagramType)
          ? departureDiagramType
          : 'unknown',
        showUniformLayout: Boolean(features.SHOW_UNIFORM_LAYOUT),
        legacyTransport: Boolean(features.USE_LEGACY_LOCK_TICKETS),
      };

      let departureSeats = null;
      const hasExplicitSeatReferences =
        selectedSeatReferences !== undefined &&
        selectedSeatReferences !== null &&
        (!Array.isArray(selectedSeatReferences) || selectedSeatReferences.length > 0);
      if (hasExplicitSeatReferences) {
        departureSeats = resolveQuickPurchaseSeatReferences({
          ...referenceOptions,
          references: selectedSeatReferences,
        });
      } else if (selectedSeatNumbers?.length) {
        departureSeats = resolveQuickPurchaseSeatReferences({
          ...referenceOptions,
          references: selectedSeatNumbers.map((number) => ({ number })),
        });
      }

      let smartSelection = null;
      if (!departureSeats) {
        const hasReturnScope = Boolean(
          purchaseAfterDetails.returns?.layout || purchaseAfterDetails.returns?.fragments?.length,
        );
        const supportedSmartScope =
          purchaseAfterDetails.departs.fragments?.length === 1 &&
          !purchaseAfterDetails.departs.hasConnections &&
          !hasReturnScope;
        const automaticSelection = selectQuickPurchaseSeats({
          layout: departureLayout,
          tripSlug: departureTripSlug,
          passengerCount,
          smartSeatSelectionEnabled: smartSeatSelectionEnabled === true,
          supportedSmartScope,
          legacyTransport: Boolean(features.USE_LEGACY_LOCK_TICKETS),
          diagramType: referenceOptions.diagramType,
          showUniformLayout: referenceOptions.showUniformLayout,
        });
        departureSeats = automaticSelection?.seats || null;
        smartSelection = automaticSelection?.smartSelection || null;
      }

      if (!isCurrent()) return supersededResult();
      if (!departureSeats) {
        errorMonitoring.notify({
          error: new Error('NOT_ENOUGH_SEATS_DEPARTURE'),
          info: { from: 'quickPurchase:seatSelection', token, passengerCount },
        });
        return { success: false, error: 'NOT_ENOUGH_SEATS_DEPARTURE', token };
      }

      if (!isCurrent()) return supersededResult();
      dispatch(selectSeats('departs', departureSeats));

      // 2b. Auto-select seats for return (if round trip)
      let returnSeats = null;
      if (purchaseAfterDetails.returns?.layout) {
        const returnLayout = purchaseAfterDetails.returns.layout;
        const returnTripSlug = purchaseAfterDetails.returns.tripSlug;
        returnSeats = autoSelectSeats(returnLayout, returnTripSlug, passengerCount);

        if (!isCurrent()) return supersededResult();
        if (!returnSeats) {
          errorMonitoring.notify({
            error: new Error('NOT_ENOUGH_SEATS_RETURN'),
            info: { from: 'quickPurchase:seatSelection', token, passengerCount },
          });
          return { success: false, error: 'NOT_ENOUGH_SEATS_RETURN', token };
        }

        if (!isCurrent()) return supersededResult();
        dispatch(selectSeats('returns', returnSeats));
      }

      // 3. Build fields for savePassengersWithTickets
      if (!isCurrent()) return supersededResult();
      if (!adapted.length) {
        errorMonitoring.notify({
          error: new Error('No passengers available for quick purchase'),
          info: { from: 'quickPurchase:noPassengers', token },
        });
        return { success: false, error: 'NO_PASSENGERS_AVAILABLE' };
      }

      const firstPassenger = adapted[0];
      const quickPassengers = adapted.slice(0, passengerCount);
      const insuranceSelection = getQuickPurchaseInsuranceSelection({
        features,
        purchase: purchaseAfterDetails,
        selected: features.PASSENGER_INSURANCE_BY_DEFAULT,
        passengers: quickPassengers,
      });
      const fields = {
        firstName: firstPassenger.firstName,
        lastName: firstPassenger.lastName,
        secondLastName: firstPassenger.secondLastName || '',
        email: firstPassenger.email,
        phone: firstPassenger.phone,
        phoneCountry: firstPassenger.phoneCountry || 'MX',
        phoneCode: firstPassenger.phoneCode || '',
        documentType: firstPassenger.documentType || '',
        documentId: firstPassenger.documentId || '',
        passengers: quickPassengers.map((fp) => ({
          firstName: fp.firstName || '',
          lastName: fp.lastName || '',
          secondLastName: fp.secondLastName || '',
          email: fp.email || '',
          phone: fp.phone || '',
          phoneCountry: fp.phoneCountry || 'MX',
          phoneCode: fp.phoneCode || '',
          documentType: fp.documentType || '',
          documentId: fp.documentId || '',
          nationality: fp.nationality || '',
          dateOfBirth: fp.dateOfBirth || '',
          gender: fp.gender || '',
          id: '',
          category: 'adult',
          busCategory: 'general',
          isFrequentPassenger: Boolean(fp.isFrequentPassenger),
          preferredPassengerOrder: true,
          ...insuranceSelection,
        })),
      };

      // 4. Build trips object
      const trips = {
        departs: purchaseAfterDetails.departs.fragments.map((frag) => ({
          id: frag.id,
          type: purchaseAfterDetails.departs.details?.transportType || 'bus',
          selectedSeats: departureSeats,
        })),
      };

      if (returnSeats && purchaseAfterDetails.returns?.fragments) {
        trips.returns = purchaseAfterDetails.returns.fragments.map((frag) => ({
          id: frag.id,
          type: purchaseAfterDetails.returns.details?.transportType || 'bus',
          selectedSeats: returnSeats,
        }));
      }

      const retainedSmartSelection = smartSelection
        ? smartSeatSelectionResult.retainSelection({
            handle: ownership,
            token,
            itineraryKey: ownership.itineraryKey,
            trips,
            smartSelection,
            onAttemptCanceled: (loadingOwnerId) =>
              dispatch(releaseQuickPurchaseLoading(loadingOwnerId)),
          })
        : null;

      // 5. Create passengers and lock tickets (awaits lockTickets to prevent race condition)
      if (!isCurrent()) return supersededResult();
      const saveResult = await savePassengersForQuickPurchase(dispatch, token, fields, trips, {
        isCurrent: callerIsCurrent,
        ownership,
        attemptOwnership,
        smartSelection: retainedSmartSelection ? smartSelection : null,
        smartSelectionContext: retainedSmartSelection,
        initialization: ownership,
      });
      if (saveResult?.superseded || !isCurrent()) return supersededResult();

      return retainedSmartSelection ? { success: true, smartSelection } : { success: true };
    } catch (error) {
      if (!isCurrent()) return supersededResult();
      errorMonitoring.notify({
        error,
        info: {
          from: 'completeQuickPurchase',
          token,
          passengers,
        },
      });
      // A refused passenger field is the one orchestration failure the buyer can actually fix, so
      // it must not degrade to a plain string like the rest. Hand the parsed error back: it tells
      // QuickCheckout to open the inline passengers form instead of the terminal error screen, and
      // carries what has to be re-dispatched once that form has mounted — redux-form's INITIALIZE
      // clears `submitErrors` and `touched`, so the dispatch made during the save above cannot
      // survive into a form that mounts afterwards.
      return {
        success: false,
        error: quickPurchaseErrorMessage(error),
        fieldError: parsePassengerFieldErrors(error),
      };
    }
  };
}

/**
 * updateQuickPurchaseSeats action creator.
 * Re-locks the departure seats of an in-progress quick purchase WITHOUT re-saving passengers.
 * Reads the current passengers and departure fragments from the store, rebuilds the `trips`
 * object with the new seats and relocks via lockTickets. lockTickets internally refreshes the
 * purchase with preservePaymentOptionSelection, so price/summary update while the payment
 * option and entered card data are preserved. Single-trip only (no returns).
 * @param {string} token - Purchase token.
 * @param {Array<object>} [departureSeats] - Selected seat objects. Falls back to the store.
 * @param {object} [options] Update behavior.
 * @param {boolean} [options.savePreference=false] Save an explicit inline confirmation.
 * @returns {Function} Redux thunk.
 */
export function updateQuickPurchaseSeats(token, departureSeats, { savePreference = false } = {}) {
  return async (dispatch) => {
    const { features } = store.getState().whitelabelConfig;
    const purchaseState = store.getState().purchase.toJS();
    const { passengers, departs } = purchaseState;
    const seats = departureSeats || departs.selectedSeats;

    if (savePreference) {
      saveConfirmedSeatPreference({
        layout: departs.layout,
        selectedSeats: seats,
        passengerCount: Array.isArray(passengers) ? passengers.length : null,
        tripSlug: departs.tripSlug || departs.fragments?.[0]?.id,
        diagramType: ['bus', 'van'].includes(departs.diagramType) ? departs.diagramType : 'unknown',
        showUniformLayout: Boolean(features.SHOW_UNIFORM_LAYOUT),
      });
    }

    // Ensure the store holds the seats we are about to lock (the seat selector already ran
    // selectSeats, this is defensive for callers that pass seats explicitly).
    dispatch(selectSeats('departs', seats));

    const trips = {
      departs: departs.fragments.map((frag) => ({
        id: frag.id,
        type: departs.details?.transportType || 'bus',
        selectedSeats: seats,
      })),
    };

    if (features.USE_LEGACY_LOCK_TICKETS) {
      const seatNumbers = seats?.map(({ number }) => number);
      await dispatch(lockTicketsLegacy('departs', token, trips.departs, passengers, seatNumbers));
      return;
    }

    await dispatch(lockTickets(token, trips, passengers, { preservePaymentOptionSelection: true }));
  };
}

/**
 * Updates purchase-level insurance in Quick Purchase and refreshes the seat lock.
 * @param {string} token - Purchase token.
 * @param {boolean} wants - Whether the user wants purchase-level insurance.
 * @returns {Function} Redux thunk.
 */
export function updateQuickPurchaseInsurance(token, wants) {
  return async (dispatch) => {
    dispatch(toggleInsurance(true));

    try {
      const purchaseResponse = await purchaseApi.update(token, { wants_insurance: wants });
      dispatch(receivePurchase(purchaseResponse));
      await dispatch(updateQuickPurchaseSeats(token));
    } catch (error) {
      dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
      errorMonitoring.notify({
        error,
        info: { from: 'quickPurchase:updateInsurance', token, wants },
      });
    } finally {
      dispatch(toggleInsurance(false));
    }
  };
}

/**
 * Updates all quick-checkout passengers with the selected passenger-level insurance state.
 * This is the quick-purchase equivalent of the passengers-step insurance controls.
 * @param {string} token - Purchase token.
 * @param {boolean} wants - Whether the user wants passenger insurance.
 * @returns {Function} Redux thunk.
 */
export function updateQuickPurchasePassengerInsurance(token, wants) {
  return async (dispatch) => {
    const {
      whitelabelConfig: { features },
      purchase,
    } = store.getState();
    const purchaseState = purchase.toJS();
    const insuranceSelection = getQuickPurchaseInsuranceSelection({
      features,
      purchase: purchaseState,
      selected: wants,
      passengers: purchaseState.passengers,
    });

    const passengersPayload = decamelizeKeys(
      purchaseState.passengers.map((passenger) => {
        return serializeQuickPurchasePassenger({
          passenger,
          features,
          insuranceSelection,
        });
      }),
    );

    dispatch(toggleInsurance(true));

    try {
      const payload = await purchaseApi.updatePassengers(token, passengersPayload);
      const { passengers } = camelizeKeys(payload);
      dispatch(setPassengers(passengers, token));
      // IAMSA keeps insurance data from the previous seat lock. Re-lock after every change so
      // the upstream lock matches the user's latest selection when enabling or disabling it.
      await dispatch(updateQuickPurchaseSeats(token));
    } catch (error) {
      dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
      errorMonitoring.notify({
        error,
        info: { from: 'quickPurchase:updatePassengerInsurance', token, wants },
      });
    } finally {
      dispatch(toggleInsurance(false));
    }
  };
}
